import { z } from 'zod';

/**
 * Shared across create/edit — plain `.url()` alone accepts `javascript:`/
 * `data:` schemes; validated here, at the write boundary, so a value that
 * will render into an `<img src>` once a future catalog surface displays
 * `previewImageUrl` can never carry one. A single `.refine()` (not a
 * `.url().or(z.literal(''))` union) so the custom message actually surfaces
 * — zod v3 replaces a failed union's branch messages with a generic
 * "invalid_union" error, which would otherwise hide this one.
 */
const httpsUrlField = z
  .string()
  .max(2048, 'Must be 2048 characters or fewer')
  .refine((v) => v === '' || /^https:\/\/[^\s]+$/i.test(v), 'Must be an https:// URL')
  .optional();

/** Create — full field set. `code`/`amountInPaise` validation mirrors backend §5's rules exactly. */
export const createProductSchema = z.object({
  code: z
    .string()
    .min(1, 'Code is required')
    .max(64, 'Code must be 64 characters or fewer')
    .regex(/^[A-Z0-9_]+$/, 'Code must be uppercase letters, numbers, and underscores only'),
  displayName: z.string().min(1, 'Display name is required').max(120),
  templateId: z.string().min(1, 'Select a template'),
  // Entered as rupees in the UI, converted to integer paise in the submit
  // handler — never in the schema — so validation messages stay in rupee
  // terms for the admin (architecture §7.2/§7.4).
  // Whole rupees only — every price display in both apps rounds to 0 fraction
  // digits (`formatPrice`), so a fractional rupee here would silently create
  // a Collection whose stored paise amount never matches what any UI shows.
  amountInRupees: z.coerce
    .number()
    .int('Enter a whole rupee amount')
    .min(500, 'Price must be at least ₹500 (ADR-019 sale floor)')
    .max(1_000_000, 'Price is too large'),
  currency: z.literal('INR'),
  description: z.string().max(2000).optional().or(z.literal('')),
  previewImageUrl: httpsUrlField,
  displayOrder: z.coerce.number().int('Enter a whole number').default(0),
  /** Issue #435 (Phase 2): tier tag */
  tier: z
    .enum(['BASIC', 'STANDARD', 'PREMIUM', 'ROYAL', 'SIGNATURE', 'ROYAL_STANDARD'])
    .optional()
    .or(z.literal('')),
});
export type CreateProductFormInput = z.infer<typeof createProductSchema>;

/**
 * Edit — allow-list only (backend §13's mass-assignment protection). No
 * `code`/`amountInPaise`/`version` field exists here at all, not just
 * disabled — matches architecture §7.3 exactly.
 */
export const editProductSchema = z.object({
  displayName: z.string().min(1, 'Display name is required').max(120),
  templateId: z.string().min(1, 'Select a template'),
  description: z.string().max(2000).optional().or(z.literal('')),
  previewImageUrl: httpsUrlField,
  displayOrder: z.coerce.number().int('Enter a whole number'),
  /** Issue #435 (Phase 2): tier tag */
  tier: z
    .enum(['BASIC', 'STANDARD', 'PREMIUM', 'ROYAL', 'SIGNATURE', 'ROYAL_STANDARD'])
    .optional()
    .or(z.literal('')),
});
export type EditProductFormInput = z.infer<typeof editProductSchema>;

/**
 * Reprice — a distinct, single-field action (architecture §7.4), never part
 * of the edit form. Rupees in the schema; paise over the wire. Whole rupees
 * only — see `createProductSchema.amountInRupees`'s comment for why.
 */
export const repriceProductSchema = z.object({
  amountInRupees: z.coerce
    .number()
    .int('Enter a whole rupee amount')
    .min(500, 'Price must be at least ₹500 (ADR-019 sale floor)')
    .max(1_000_000, 'Price is too large'),
});
export type RepriceProductInput = z.infer<typeof repriceProductSchema>;
